import { Button, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DIALOGS from 'app/constants/dialogs';
import { openDialog } from 'app/store/fuse/dialogSlice';
import { selectors } from 'app/main/billing/store/billingSlice';
import { PATHS, SUBSCRIPTION_STATUS, TRANSLATION_KEY } from 'app/main/billing/constants';
import STATUS_CONFIG from 'app/main/billing/subscriptionStatusConfig';
import { calculateTrialDaysLeft, isTrialExpired } from 'app/main/billing/utils';

const BANNER_STYLE = {
  active: { border: '#D4D8F7', background: '#F7F8FE', text: '#5D3DD4' },
  ended: { border: '#FECDCA', background: '#FEF3F2', text: '#B42318' },
};

export default function SubscriptionStatusBanner() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation(TRANSLATION_KEY);
  const subscription = useSelector(selectors.selectCurrentSubscription);
  const serverTime = useSelector(selectors.selectServerTime);

  const expired = isTrialExpired(subscription, serverTime);
  const isTrialing = subscription?.status === SUBSCRIPTION_STATUS.TRIALING;
  // Every status the billing sidebar warns about is worth repeating on the other pages,
  // where the user has nothing else telling them the subscription needs attention.
  const statusWarning = STATUS_CONFIG[subscription?.status]?.sidebarWarning;
  const showStatusWarning = Boolean(statusWarning) && !location.pathname.startsWith(PATHS.BILLING);

  if (!subscription || (!isTrialing && !expired && !showStatusWarning)) {
    return null;
  }

  const openPricingPlans = () => {
    dispatch(openDialog({ dialogId: DIALOGS.UPGRADE_PLAN }));
  };

  const goToBilling = () => {
    navigate(PATHS.BILLING);
  };

  let style;
  let message;
  let action;

  if (isTrialing && !expired) {
    const trialEndDate = subscription.trialEndedAt || subscription.endDate;
    const daysRemaining = calculateTrialDaysLeft(trialEndDate, serverTime);

    style = BANNER_STYLE.active;
    message = (
      <>
        <strong>{t('BANNER_TRIAL_ACTIVE_TITLE', { days: daysRemaining })}</strong>{' '}
        {t('BANNER_TRIAL_ACTIVE_TEXT')}
      </>
    );
    action = { label: t('VIEW_PRICING_PLANS'), onClick: openPricingPlans };
  } else if (expired) {
    style = BANNER_STYLE.ended;
    message = (
      <>
        <strong>{t('BANNER_TRIAL_ENDED_TITLE')}</strong> {t('BANNER_TRIAL_ENDED_TEXT')}
      </>
    );
    action = { label: t('VIEW_PRICING_PLANS'), onClick: openPricingPlans };
  } else {
    style = BANNER_STYLE.ended;
    message = <strong>{t(statusWarning)}</strong>;
    action = { label: t('BANNER_GO_TO_BILLING'), onClick: goToBilling };
  }

  return (
    <div
      style={{
        height: '40px',
        width: '100%',
        boxSizing: 'border-box',
        border: `1px solid ${style.border}`,
        backgroundColor: style.background,
        paddingTop: '11px',
        paddingBottom: '11px',
        display: 'flex',
        justifyContent: 'center',
        gap: '8px',
        alignItems: 'center',
      }}
    >
      <Typography variant="bodyMMedium" sx={{ color: style.text }}>
        {message}
      </Typography>

      <Button variant="contained" color="primary" className="h-[24px]" onClick={action.onClick}>
        {action.label}
      </Button>
    </div>
  );
}
